import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const sessionId = params.get("session_id");
  const orderNumber = params.get("order");
  const [state, setState] = useState("checking");

  useEffect(() => {
    clear();
    if (!sessionId) { setState(orderNumber ? "paid" : "unknown"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("failed"); return; }
      } catch { /* keep trying */ }
      if (++tries < 8) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-background"><Header />
      <div className="max-w-xl mx-auto text-center py-24 px-4" data-testid="payment-success-page">
        {state === "checking" ? (
          <><Loader2 className="mx-auto animate-spin text-accent" size={40} />
            <p className="mt-4 text-muted-foreground">Confirming your payment…</p></>
        ) : state === "failed" ? (
          <><h1 className="text-2xl font-bold text-red-600">Payment not completed</h1>
            <p className="mt-3 text-muted-foreground">Your payment didn't go through. You can try again from checkout.</p>
            <Button className="mt-6 bg-steel text-white" onClick={() => navigate("/checkout")}>Back to Checkout</Button></>
        ) : (
          <><CheckCircle2 className="mx-auto text-emerald-600" size={56} />
            <h1 className="mt-4 text-3xl font-extrabold">Thank you — payment received</h1>
            <p className="mt-3 text-muted-foreground">
              {orderNumber ? <>Your order <strong>{orderNumber}</strong> is confirmed and now being processed.</> : "Your order is confirmed and now being processed."}
              {state === "pending" && " (Final confirmation may take a moment.)"}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              {orderNumber && <Button className="bg-accent text-steel font-semibold" data-testid="view-order-btn" onClick={() => navigate(`/order/${orderNumber}`)}>View Order</Button>}
              <Button variant="outline" onClick={() => navigate("/")}>Continue Shopping</Button>
            </div></>
        )}
      </div>
    </div>
  );
}
